var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@animech/ui";
import * as Styled from "./flow-restriction-box.style";
import { FlowRestrictionFindings } from "./flow-restriction-findings";
import { getFlowRestrictionColor } from "./flow-restriction-findings.style";
import { useAppSelector, selectors } from "store";
import { FlowRestrictionIcon } from "ui/components/elements/icons/flow-restriction";
import { CupOnObjectIcon } from "ui/components/elements/icons/cup-on-object";
import { getWorstFindingSeverity, needsAttention, } from "utility/flow-restrictions";
var findGroup = function (side, state) {
    return side.groups.find(function (group) { return group.state === state && group.count > 0; });
};
var hasGroup = function (side, state) {
    return findGroup(side, state) !== undefined;
};
/* Valves in their intended state during the transfer function as intended
 * and are green, also with a critical or warning finding on them; then the
 * bullet only states their state and points to the finding */
var getIntendedStateBullet = function (group, key, t) { return ({
    text: t(needsAttention(group.findingSeverity) ? "".concat(key, "_with_findings") : key),
    severity: "ok",
}); };
/* One bullet per group of valves, so a side where only some valves fail gets
 * both its verdicts. The verdicts come first, then what to do about the
 * failing groups, unless the calculation API's findings say that instead. */
export var getFlowRestrictionBullets = function (summary, t, withAdvice) {
    if (withAdvice === void 0) { withAdvice = true; }
    var onObject = summary.onObject, offObject = summary.offObject;
    var bullets = [];
    [
        [onObject, "open", "flow_restrictions.on_object_open"],
        [offObject, "closed", "flow_restrictions.off_object_closed"],
        [offObject, "restrictor", "flow_restrictions.off_object_restrictor"],
    ].forEach(function (_a) {
        var _b = __read(_a, 3), side = _b[0], state = _b[1], key = _b[2];
        var group = findGroup(side, state);
        if (group)
            bullets.push(getIntendedStateBullet(group, key, t));
    });
    if (hasGroup(offObject, "open")) {
        bullets.push({
            text: t("flow_restrictions.off_object_open"),
            severity: "critical",
        });
        if (withAdvice) {
            bullets.push({
                text: t("flow_restrictions.off_object_open_tubing_advice"),
                severity: "critical",
            }, {
                text: t("flow_restrictions.off_object_open_valve_advice"),
                severity: "critical",
            });
        }
    }
    if (hasGroup(onObject, "closed")) {
        bullets.push({
            text: t("flow_restrictions.on_object_closed"),
            severity: "critical",
        });
        if (withAdvice) {
            bullets.push({
                text: t("flow_restrictions.on_object_closed_advice"),
                severity: "critical",
                note: t("flow_restrictions.on_object_closed_note"),
            });
        }
    }
    return bullets;
};
/* Three groups (open, closed and restrictors on one side) need smaller icons */
var getIconSize = function (side) {
    return side.groups.length > 2 ? 26 : 36;
};
var SideIcons = function (_a) {
    var side = _a.side;
    var theme = useTheme();
    var size = getIconSize(side);
    return (_jsx(Styled.Groups, { children: side.groups.map(function (group) { return (_jsx(Styled.Group, { width: size, children: _jsx(FlowRestrictionIcon, { state: group.state, color: group.count === 0
                    ? theme.colors.palette.grey[600]
                    : getFlowRestrictionColor(theme, group.functionsAsIntended), size: size }) }, group.state)); }) }));
};
/* Lined up under SideIcons */
var SideCounts = function (_a) {
    var side = _a.side;
    var t = useTranslation().t;
    var size = getIconSize(side);
    return (_jsx(Styled.Groups, { children: side.groups.map(function (group) { return (_jsx(Styled.Group, { width: size, children: _jsx(Styled.Count, { children: t("flow_restrictions.count", { count: group.count }) }) }, group.state)); }) }));
};
/**
 * Whether the sense valves close on the cups outside of the part and stay
 * open on the cups holding it, as calculated, and the calculation API's
 * findings on what to improve. Sits on top of the lifting performance box and
 * is shown for a settled result with sense valves or restrictors, also a
 * failed one (see selectShownOrWithheldPerformanceData). A result with a
 * critical or warning finding outlines the box in its colour, pulses it and
 * unfolds the details again. The details can be folded away to free up the
 * canvas; the box stays mounted while hidden, so that choice survives
 * recalculations without problems.
 */
export var FlowRestrictionBox = function () {
    var t = useTranslation().t;
    var summary = useAppSelector(selectors.selectFlowRestrictionSummary);
    var isLoading = useAppSelector(selectors.selectIsPerformanceLoading);
    var isGraphPanelOpen = useAppSelector(selectors.selectIsGraphPanelOpen);
    var findingGroups = useAppSelector(selectors.selectFlowRestrictionFindingGroups);
    var _a = __read(useState(true), 2), isExpanded = _a[0], setIsExpanded = _a[1];
    /* The ones working as intended are what the verdict bullets say already */
    var problems = (findingGroups !== null && findingGroups !== void 0 ? findingGroups : []).filter(function (_a) {
        var severity = _a.severity;
        return severity !== "ok";
    });
    var worstSeverity = getWorstFindingSeverity(problems);
    var attentionSeverity = needsAttention(worstSeverity)
        ? worstSeverity
        : undefined;
    useEffect(function () {
        if (attentionSeverity)
            setIsExpanded(true);
    }, [findingGroups, attentionSeverity]);
    if (!summary || isLoading || isGraphPanelOpen) {
        return null;
    }
    var onObject = summary.onObject, offObject = summary.offObject, codes = summary.codes;
    var bullets = getFlowRestrictionBullets(summary, t, !findingGroups);
    var hasDetails = bullets.length > 0 || problems.length > 0;
    return (_jsxs(Styled.FlowRestrictionBox, { "data-testid": "flow-restriction-box", attentionSeverity: attentionSeverity, children: [_jsxs(Styled.Header, { children: [_jsx(Styled.Title, { children: t("flow_restrictions.title") }), hasDetails && (_jsx(Styled.ExpandToggle, { isExpanded: isExpanded, onClick: function () { return setIsExpanded(!isExpanded); }, "aria-label": t(isExpanded
                            ? "flow_restrictions.hide_details"
                            : "flow_restrictions.show_details"), "aria-expanded": isExpanded, children: _jsx(Icon, { name: "chevron-up", color: "primary" }) }))] }), _jsxs(Styled.Diagram, { children: [_jsx(Styled.Pictogram, { children: _jsx(CupOnObjectIcon, { onObject: true, label: t("flow_restrictions.on_object") }) }), _jsx(Styled.Divider, {}), _jsx(Styled.Pictogram, { children: _jsx(CupOnObjectIcon, { onObject: false, label: t("flow_restrictions.off_object") }) }), _jsx(SideIcons, { side: onObject }), _jsx(Styled.Codes, { children: codes.map(function (code) { return (_jsx("div", { children: code }, code)); }) }), _jsx(SideIcons, { side: offObject }), _jsx(SideCounts, { side: onObject }), _jsx(Styled.Divider, {}), _jsx(SideCounts, { side: offObject })] }), isExpanded && bullets.length > 0 && (_jsx(Styled.Bullets, { children: bullets.map(function (_a) {
                    var text = _a.text, severity = _a.severity, note = _a.note;
                    return (_jsxs(Styled.Bullet, { severity: severity, children: [text, note && _jsx(Styled.Note, { children: note })] }, text));
                }) })), isExpanded && problems.length > 0 && (_jsx(FlowRestrictionFindings, { groups: problems }))] }));
};
